import { i18n } from "@/utils/i18n";

type Position = "none" | "root" | "middle" | "last";

export class Threader {
  static compiler() {
    // 1. 新回复插入后修正 Position 和竖线
    up.on("comments:reply", (event: any) => {
      const { parent, element } = event;
      if (!parent || !element) {
        console.warn("[Threader] comments:reply 缺少 parent 或 element", {
          parent,
          element,
        });
        return;
      }
      console.info(
        "[Threader] 触发 comments:reply，准备修正线程。被回复评论 ID:",
        parent.id,
      );
      Threader.correctOnReply(parent, element);
    });

    // 2. 评论删除后（软删除，DOM 结构不变，无需操作）
    up.on("comments:deleted", (event: any) => {
      // 软删除策略：Unpoly 已将内容替换为"已删除"占位符
      // data-position 不变，竖线不受影响
      console.info(
        "[Threader] comments:deleted 触发，软删除完成，评论 ID:",
        event.commentId,
      );
    });
  }

  /**
   * 根据状态机修正 data-position 并重新渲染竖线
   */
  private static correctOnReply(
    parent: HTMLElement,
    newReply: HTMLElement,
  ): void {
    const parentPos = parent.dataset.position as Position;
    console.info(
      `[Threader] correctOnReply 执行，当前被回复评论(${parent.id}) 状态: ${parentPos}`,
    );

    switch (parentPos) {
      case "none":
        // 被回复评论：none -> root
        console.info(
          "[Threader] 状态机 [none]: 父节点 none->root，子节点 ->last",
        );
        Threader.setPosition(parent, "root");
        // 新回复：middle -> last（修正服务端返回值）
        Threader.setPosition(newReply, "last");
        break;

      case "root":
        console.info(
          "[Threader] 状态机 [root]: 父节点不变，子节点保持中间态(middle)",
        );
        // 被回复评论：不变
        // 新回复：middle（服务端已正确，无需修正）
        break;

      case "middle":
        console.info(
          "[Threader] 状态机 [middle]: 父节点不变，子节点保持中间态(middle)",
        );
        // 被回复评论：不变
        // 新回复：middle（服务端已正确，无需修正）
        break;

      case "last":
        // 被回复评论：last -> middle
        console.info(
          "[Threader] 状态机 [last]: 父节点 last->middle，子节点 ->last",
        );
        Threader.setPosition(parent, "middle");
        // 新回复：middle -> last（修正服务端返回值）
        Threader.setPosition(newReply, "last");
        break;
      default:
        console.warn(`[Threader] 未知的父节点状态: ${parentPos}`);
        break;
    }
  }

  /**
   * 设置元素的 data-position 并渲染对应竖线
   */
  private static setPosition(el: HTMLElement, pos: Position): void {
    const oldPos = el.dataset.position as Position;
    console.info(
      `[Threader] setPosition: 元素(${el.id}) 数据从 ${oldPos || "未知"} 修改为 ${pos}`,
    );
    el.dataset.position = pos;
    Threader.renderThreadLine(el, oldPos, pos);
  }

  /**
   * 根据 position 变化动态创建/替换竖线 DOM
   */
  private static renderThreadLine(
    el: HTMLElement,
    oldPos: Position,
    newPos: Position,
  ): void {
    if (oldPos === newPos) {
      console.info(
        `[Threader] renderThreadLine: 状态无变化(${newPos}), 跳过 DOM 渲染`,
      );
      return;
    }

    const commentId = el.id.replace("comment-", "");
    const avatarCol = el.querySelector(
      ".relative.shrink-0.flex.flex-col.items-center",
    );
    if (!avatarCol) {
      console.warn(
        `[Threader] renderThreadLine: 未能在 ${el.id} 找到头像列容器`,
      );
      return;
    }

    // 获取半截竖线和全高竖线的标记（根据高度区分）
    const getHalfLine = () => avatarCol.querySelector("div.h-4.sm\\:h-5");
    const getFullLine = () =>
      Array.from(avatarCol.querySelectorAll("div.absolute")).find(
        (d) => !d.classList.contains("h-4") && d.classList.contains("w-0.5"),
      );

    // last -> middle (半截竖线 -> 全高竖线)
    if (oldPos === "last" && newPos === "middle") {
      console.info(
        `[Threader] DOM渲染 [last->middle]: 将 ${el.id} 替换为全高贯穿竖线`,
      );
      const halfLine = getHalfLine();
      if (halfLine) {
        const fullLine = document.createElement("div");
        fullLine.className = "absolute top-0 bottom-0 w-0.5 bg-primary/30";
        halfLine.replaceWith(fullLine);
      } else {
        console.warn(`[Threader] DOM渲染警告: 未在 ${el.id} 找到半截竖线`);
      }
    }

    // middle -> last (全高竖线 -> 半截竖线)
    else if (oldPos === "middle" && newPos === "last") {
      console.info(
        `[Threader] DOM渲染 [middle->last]: 将 ${el.id} 替换为半截截止竖线`,
      );
      const fullLine = getFullLine();
      if (fullLine) {
        const halfLine = document.createElement("div");
        halfLine.className = "absolute top-0 h-4 sm:h-5 w-0.5 bg-primary/30";
        fullLine.replaceWith(halfLine);
      } else {
        console.warn(`[Threader] DOM渲染警告: 未在 ${el.id} 找到全高竖线`);
      }
    }

    // none -> root (动态创建折叠线 + 按钮 + 底部摘要)
    else if (oldPos === "none" && newPos === "root") {
      console.info(
        `[Threader] DOM渲染 [none->root]: 为 ${el.id} 动态生成并挂载折叠组件与 L型连线辅助元素`,
      );

      // 1. 在头像 div 之后添加折叠线 + 按钮
      const avatarDiv = avatarCol.querySelector("div.z-10");
      if (avatarDiv) {
        const expandedDiv = document.createElement("div");
        expandedDiv.className =
          "replies-line-expanded flex flex-col items-center flex-1 w-full mt-1";
        expandedDiv.innerHTML = `
          <div class="w-0.5 flex-1 bg-primary/30"></div>
          <button type="button" data-replies-toggle="${commentId}" class="relative z-10 flex items-center justify-center w-5 h-5 my-0.5 text-muted hover:text-primary transition-gentle cursor-pointer" aria-expanded="true" aria-controls="replies-${commentId}" title="${i18n.t("comments.reply_plural")}">
            <svg class="w-4 h-4"><use href="#icon-minus-circle"></use></svg>
          </button>
        `;

        const collapsedDiv = document.createElement("div");
        collapsedDiv.className =
          "replies-line-collapsed hidden flex-1 w-full bg-linear-to-b from-primary/30 to-primary/30 bg-size-[2px_100%] bg-no-repeat bg-center";

        avatarDiv.insertAdjacentElement("afterend", collapsedDiv);
        avatarDiv.insertAdjacentElement("afterend", expandedDiv);
      } else {
        console.warn(
          `[Threader] DOM渲染警告: 未能在 ${el.id} 的头像列中找到主头像挂载锚点(div.z-10)`,
        );
      }

      // 2. 在 reply-slot 之后添加底部折叠摘要
      const replySlot = el.querySelector(`#reply-slot-${commentId}`);
      if (replySlot) {
        const summaryDiv = document.createElement("div");
        summaryDiv.className = "replies-line-collapsed hidden -mt-3 mb-4";
        summaryDiv.innerHTML = `
          <div class="flex items-center gap-3">
            <div class="relative shrink-0 w-8 sm:w-10 h-6">
              <div class="absolute top-0 bottom-1/2 left-1/2 w-4 border-l-2 border-b-2 border-primary/30 rounded-bl-lg -translate-x-[1px]"></div>
            </div>
            <div class="flex-1 flex items-center">
              <button type="button" data-replies-toggle="${commentId}" class="group flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary/80 transition-gentle cursor-pointer" aria-expanded="false" aria-controls="replies-${commentId}">
                <svg class="w-3.5 h-3.5"><use href="#icon-plus-circle"></use></svg>
                <span>1 ${i18n.t("comments.reply_singular")}</span>
                <svg class="w-3.5 h-3.5 transform group-hover:translate-x-0.5 transition-transform"><use href="#icon-chevron-right"></use></svg>
              </button>
            </div>
          </div>
        `;
        replySlot.insertAdjacentElement("afterend", summaryDiv);
      } else {
        console.warn(
          `[Threader] DOM渲染警告: 未能在 ${el.id} 找到回复插槽(#reply-slot-${commentId})`,
        );
      }
    }
  }
}
